/* One-shot localStorage hydration for the mock build.
 *
 *   import { seedMockStore } from "@/data/mocks/seed";
 *   seedMockStore();   // call once at app boot
 *
 * Each domain key is only written if missing. Idempotent — safe to call
 * on every load. `resetMockStore()` wipes everything and reseeds.
 */

import * as store from "./storage";
import { DEMO_USERS } from "./users";
import { seedReviews } from "./reviews";

const SEEDED_FLAG = "seeded";

const SEED_ADDRESSES = [
  { id: "a-1", label: "Pharmacy — Main",   line1: "Shop 12, Linking Road",  line2: "Bandra West", city: "Mumbai", state: "MH", pin: "400050", phone: "+91 98201 12345", default: true,  type: "Business" },
  { id: "a-2", label: "Warehouse — Tirunelveli", line1: "Opposite To Ramar Kovil, South Bazaar", line2: "Palayamkottai", city: "Tirunelveli", state: "TN", pin: "627002", phone: "+91 98201 67890", default: false, type: "Business" },
];

function daysAgo(days) {
  return new Date(Date.now() - days * 86_400_000).toISOString();
}

function seedEnquiries() {
  const cust = DEMO_USERS.find((u) => u.id === "u-customer");
  if (!cust) return [];
  return [
    {
      id: "ENQ-2026-0001",
      user_id: cust.id,
      user_email: cust.email,
      status: "pending",
      placed_at: daysAgo(2),
      items: [
        { product_slug: "amoxicillin-500mg-capsules", product_name: "Amoxicillin 500mg Capsules", qty: 10, unit_price: 86, line_total: 860 },
        { product_slug: "paracetamol-ip-650mg",       product_name: "Paracetamol IP 650mg",       qty: 20, unit_price: 64, line_total: 1280 },
      ],
      sales_rep: null,
      notes: "Bulk order for our Bandra branch. Please confirm Q4 stock availability.",
      thread: [
        { id: "m-1", author: cust.email, role: cust.role, message: "Bulk order for our Bandra branch. Please confirm Q4 stock availability.", ts: daysAgo(2) },
      ],
    },
    {
      id: "ENQ-2026-0002",
      user_id: cust.id,
      user_email: cust.email,
      status: "quoted",
      placed_at: daysAgo(5),
      items: [
        { product_slug: "insulin-glargine-100-iu-ml", product_name: "Insulin Glargine 100 IU/ml", qty: 6, unit_price: 720, line_total: 4320 },
      ],
      sales_rep: "Aarav Sharma",
      notes: null,
      thread: [
        { id: "m-2", author: cust.email,            role: cust.role,    message: "Need 6 pens for monthly stock — cold-chain required.", ts: daysAgo(5) },
        { id: "m-3", author: "sales@nethrasap.in",  role: "sales",      message: "Quoted at ₹720/pen with cold-chain dispatch. Quote valid 7 days.", ts: daysAgo(4) },
      ],
    },
  ];
}

function seedNotificationsForCustomer() {
  return [
    { id: "n-1", type: "enquiry",      title: "Quote ready for ENQ-2026-0002",      body: "Aarav Sharma sent you a quote — ₹720/pen cold-chain dispatch.", read: false, link: "/pages/user/enquiry.html?id=ENQ-2026-0002", created_at: daysAgo(4), priority: "high" },
    { id: "n-2", type: "order",        title: "Welcome to Nethrasap",                body: "Browse our catalogue and place your first order to unlock retailer pricing.", read: false, link: "/pages/storefront/products.html", created_at: daysAgo(7), priority: "med" },
    { id: "n-3", type: "system",       title: "Account verified",                    body: "Your account is verified. You can now access institutional pricing.", read: true,  link: "/pages/user/profile.html", created_at: daysAgo(10), priority: "low" },
    { id: "n-4", type: "alert",        title: "Cold-chain lane MUM→BLR resolved",    body: "All affected shipments replaced — no action required.", read: true,  link: null, created_at: daysAgo(14), priority: "med" },
    { id: "n-5", type: "report",       title: "Monthly statement is ready",          body: "Download your January 2026 procurement statement from My orders.", read: true,  link: "/pages/user/orders.html", created_at: daysAgo(21), priority: "low" },
  ];
}

function seedNotificationsForAnon() {
  // Demo inbox served to logged-out storefront visitors so the page feels alive.
  return [
    { id: "ax-1", type: "order",        title: "Welcome to Nethrasap",                  body: "Sign in to track orders, save enquiries and unlock institutional pricing.", read: false, link: "/pages/auth/login.html",            created_at: daysAgo(0),  priority: "high" },
    { id: "ax-2", type: "alert",        title: "Cold-chain dispatch lane upgraded",    body: "Mumbai → Bengaluru lane now ships in 18 hours with live temp telemetry.", read: false, link: "/pages/storefront/products.html",   created_at: daysAgo(1),  priority: "med" },
    { id: "ax-3", type: "enquiry",      title: "Bulk pricing now available for retailers", body: "Get up to 18% off list when you order 50+ units of any SKU.",          read: false, link: "/pages/storefront/products.html",   created_at: daysAgo(2),  priority: "med" },
    { id: "ax-4", type: "verification", title: "New: instant CDSCO verification",      body: "Clinicians and pharmacies are now auto-verified within 4 minutes.",        read: true,  link: "/pages/auth/signup.html",           created_at: daysAgo(5),  priority: "low" },
    { id: "ax-5", type: "report",       title: "May 2026 catalogue refresh",            body: "1,247 new SKUs added across cardio, surgical and wellness lines.",       read: true,  link: "/pages/storefront/categories.html", created_at: daysAgo(9),  priority: "low" },
    { id: "ax-6", type: "system",       title: "Compare up to 4 products side-by-side", body: "Use the compare icon on any product card to line up specs and price.",   read: true,  link: "/pages/storefront/compare.html",    created_at: daysAgo(14), priority: "low" },
    { id: "ax-7", type: "message",      title: "Need help? Chat with Nethra Assist",   body: "Our concierge bot can check stock, payment, delivery — 24×7.",            read: true,  link: null,                                created_at: daysAgo(21), priority: "low" },
  ];
}

const SEED_WISHLIST_SLUGS = [
  "insulin-glargine-100-iu-ml",
  "pulse-oximeter-fingertip",
  "multivitamin-effervescent",
  "digital-bp-monitor-upper-arm",
  "amoxicillin-500mg-capsules",
];

const SEED_COMPARE_SLUGS = [
  "pulse-oximeter-fingertip",
  "digital-bp-monitor-upper-arm",
  "multivitamin-effervescent",
];

const WISHLIST_KEY = "nethrasap.wishlist";
const COMPARE_KEY  = "nethrasap.compare";

function isEmptyList(raw) {
  if (raw === null || raw === undefined) return true;
  try {
    const parsed = JSON.parse(raw);
    return !Array.isArray(parsed) || parsed.length === 0;
  } catch (_) {
    return true;
  }
}

function seedWishlistOnce() {
  if (typeof window === "undefined") return;
  // Prime when missing or empty so a stale-empty store still gets demo data.
  if (isEmptyList(window.localStorage.getItem(WISHLIST_KEY))) {
    try { window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(SEED_WISHLIST_SLUGS)); } catch (_) {}
  }
}

function seedCompareOnce() {
  if (typeof window === "undefined") return;
  if (isEmptyList(window.localStorage.getItem(COMPARE_KEY))) {
    try { window.localStorage.setItem(COMPARE_KEY, JSON.stringify(SEED_COMPARE_SLUGS)); } catch (_) {}
  }
}

// notifications.* live inside the namespaced store (not raw localStorage).
// SEEDS only writes when the key is undefined; if a previous boot wrote an
// empty `[]` we need to refresh it. Idempotent — keeps user mutations.
function reseedNotificationsIfEmpty() {
  const anon = store.get("notifications.anon", undefined);
  if (!Array.isArray(anon) || anon.length === 0) {
    store.set("notifications.anon", seedNotificationsForAnon());
  }
  const cust = store.get("notifications.u-customer", undefined);
  if (!Array.isArray(cust) || cust.length === 0) {
    store.set("notifications.u-customer", seedNotificationsForCustomer());
  }
}

const SEEDS = {
  users: () => DEMO_USERS,
  reviews: () => seedReviews(),
  cart: () => ({ items: [], coupon: null, owner: "anon" }),
  orders: () => [],
  addresses: () => SEED_ADDRESSES,
  enquiries: () => seedEnquiries(),
  user_overrides: () => ({}),
  coupons: () => [
    { code: "WELCOME10", description: "10% off, min order ₹3000", percent_off: 10, min_order: 3000, max_discount: 1000 },
    { code: "FLAT50",    description: "Flat ₹50 off",            flat_off: 50,    min_order: 500,  max_discount: 50 },
    { code: "RETAILER100", description: "Retailer perk: ₹100 off", flat_off: 100, min_order: 1000, max_discount: 100, role: "retailer" },
  ],
  "notifications.u-customer": () => seedNotificationsForCustomer(),
  "notifications.anon":       () => seedNotificationsForAnon(),
};

export function seedMockStore() {
  if (typeof window === "undefined") return;
  Object.entries(SEEDS).forEach(([domain, factory]) => {
    if (store.get(domain, undefined) === undefined) {
      store.set(domain, factory());
    }
  });
  seedWishlistOnce();
  seedCompareOnce();
  reseedNotificationsIfEmpty();
  store.set(SEEDED_FLAG, true);
}

export function resetMockStore({ reload = true } = {}) {
  store.clearAll();
  seedMockStore();
  if (reload && typeof window !== "undefined") {
    try { window.localStorage.removeItem("nethrasap.token"); } catch (_) {}
    try { window.localStorage.removeItem("nethrasap.refresh"); } catch (_) {}
    setTimeout(() => window.location.reload(), 50);
  }
}

if (typeof window !== "undefined") {
  window.resetMockStore = resetMockStore;
}
